import { Formik, FormikHelpers } from "formik";
import {
  ControlcenterMelcoModelsLicenseCreateResponse,
  useFormSubmit,
} from "melco-shared-logic";
import { useQueryClient } from "react-query";
import { DirtyFormPrompt } from "../../../form/DirtyFormPrompt";
import { DisplayUpgradeLicenseForm } from "./DisplayUpgradeLicenseForm";
import { useLicenseManagerUpgrade } from "../../../../hooks/licensemanager/useLicenseManagerUpgrade";

export type UpgradeLicenseFormValueType = {
  assign_license?: boolean;
  serial_number: string;
  email_address: string;
  melco_customer_id?: string | null;
  language?: string | null;
};

type UpgradeLicenseFormProps = {
  onUpgradeLicenseCallBackFn: (
    obj: UpgradeLicenseFormValueType,
    license: ControlcenterMelcoModelsLicenseCreateResponse | undefined
  ) => void;
};

export const UpgradeLicenseForm: React.FC<UpgradeLicenseFormProps> = ({
  onUpgradeLicenseCallBackFn,
}) => {
  const queryClient = useQueryClient();
  const queryKey = "upgradelicensemanage";
  const licenseManager = useLicenseManagerUpgrade();
  const onSubmit = async (
    values: UpgradeLicenseFormValueType,
    formikHelpers: FormikHelpers<UpgradeLicenseFormValueType>
  ) => {
    await onUpgradeSerialNumber(values, formikHelpers, {
      isManuallyResettingForm: true,
    });
  };

  const createLicenseFormToInitialValues = (
    formvalues: UpgradeLicenseFormValueType
  ) => {
    const {
      email_address,
      serial_number,
      assign_license,
      melco_customer_id,
      language,
    } = formvalues;

    const initialValues: UpgradeLicenseFormValueType = {
      serial_number: serial_number ?? "",
      email_address: email_address ?? "",
      assign_license: assign_license ?? false,
      melco_customer_id: melco_customer_id ?? "",
      language: language,
    };

    return initialValues;
  };

  const initialValues: UpgradeLicenseFormValueType = {
    serial_number: "",
    email_address: "",
    assign_license: false,
    melco_customer_id: "",
    language: "",
  };

  const handleUpgradeLicense = async (
    values: UpgradeLicenseFormValueType,
    formikHelpers: FormikHelpers<UpgradeLicenseFormValueType>,
    options: {}
  ) => {
    /**
     *
     *  * As per requirement by default we have to show Don't assign license switch button off in UI
     * but for backend we have to send true value for it.
     * so we will flip the value , whatever is shown in UI
     * if switch button off then send true
     * if switch button on then send false
     */
    //setOpenAskConfirmationBoxTrigger(!openAskConirmationBoxTrigger)
    let newEntity: ControlcenterMelcoModelsLicenseCreateResponse | undefined =
      await licenseManager({
        email_address: values.email_address,
        serial_number: values.serial_number,
        step: 1,
        assign_license: !values.assign_license,
        melco_customer_id: values.melco_customer_id,
        language: values.language,
      });
    console.log(newEntity);
    formikHelpers.resetForm({
      values: newEntity
        ? createLicenseFormToInitialValues({
            email_address: newEntity?.email_address ?? "",
            serial_number: newEntity?.serial_number ?? "",
            melco_customer_id: newEntity?.melco_customer_id ?? "",
            language: newEntity?.language,
            //"assign_license": newEntity?.assign_license ?? false
          })
        : createLicenseFormToInitialValues({
            serial_number: "",
            email_address: "",
            assign_license: false,
            melco_customer_id: "",
            language: "",
          }),
    });
    queryClient.setQueryData(queryKey, newEntity);
    onUpgradeLicenseCallBackFn(values, newEntity);
  };

  const formSubmitToUpgradeLicense = () => {
    return [
      async (
        values: UpgradeLicenseFormValueType,
        formikHelpers: FormikHelpers<UpgradeLicenseFormValueType>
      ) => {
        return handleUpgradeLicense(values, formikHelpers, {});
      },
      {
        translationPrefix: ["licensemanager", "upgrade"].join("."),
        onError: () => {
          // refetch entity from server
          queryClient.invalidateQueries(queryKey);
        },
      },
    ] as const;
  };

  const onUpgradeSerialNumber = useFormSubmit(...formSubmitToUpgradeLicense());

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        <DirtyFormPrompt>
          <DisplayUpgradeLicenseForm />
        </DirtyFormPrompt>
      </Formik>
    </>
  );
};
